import axios from 'api/axios-configure'
import { AlertBanner, AlertBannerText, Button, RadioButton, TextField } from 'monday-ui-react-core'
import './SetupAccountForm.scss'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Checkbox, Grid } from '@mui/material'
import { isValidEmail } from 'utils/commonUtils'
import { LoadingSpinner } from 'components/LoadingSpinner'

const STRING_ONLY = /^[a-zA-Z_ ]+$/
const ALPHANUMERIC_ONLY = /^[a-zA-Z0-9_ ]+$/
const NUMERIC_ONLY = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/

const ORGANIZATION_PARTY_TYPE = {
    MAIL: 'MAIL',
    ELECTRONIC: 'ELECTRONIC',
}

const SetupAccountForm = () => {

    const validateStringOnly = (field) => !STRING_ONLY.test(field)
    const validateNumberOnly = (field) => !NUMERIC_ONLY.test(field)
    const validateAlphaNumeric = (field) => !ALPHANUMERIC_ONLY.test(field)

    const navigate = useNavigate()

    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [contactMe, setContactMe] = useState(false)
    const [selectedEmailNotification, setSelectedEmailNotification] = useState()

    const [isLoading, setIsLoading] = useState(true)
    const [displayBanner, setDisplayBanner] = useState(false)
    const [bannerMessage, setBannerMessage] = useState('')
    const [bannerStyle, setBannerStyle] = useState('positive')

    const [companyStatementTypes, setCompanyStatementTypes] = useState({
        1: {
            label: 'Gas Balancing Statements',
            mail: false,
            electronic: false,
        },
        2: {
            label: 'Payout Statements',
            mail: false,
            electronic: false,
        },
        3: {
            label: 'JIB Invoices',
            mail: false,
            electronic: false,
        },
        4: {
            label: 'Revenue Checks',
            mail: false,
            electronic: false,
        },
        5: {
            label: 'Other',
            mail: false,
            electronic: false,
            other: '',
        }
    })
    const [accountingSoftware, setAccountingSoftware] = useState("")

    const clearBanner = () => {
        setDisplayBanner(false)
        setBannerMessage('')
        setBannerStyle('positive')
    }

    const displayErrorMessage = (message) => {
        setBannerMessage(message)
        setBannerStyle('negative')
        setDisplayBanner(true)
    }

    const displaySuccessMessage = (message) => {
        setBannerMessage(message)
        setBannerStyle('positive')
        setDisplayBanner(true)

        setTimeout(() => {
            clearBanner()
        }, 5000)
    }

    const handleEmailNotificationChange = (event) => {
        setSelectedEmailNotification(event.target.value)
    }

    const validateSaveChanges = () => {
        if (
            !validateStringOnly(firstName) &&
            !validateStringOnly(lastName) &&
            email != '' &&
            isValidEmail(email) &&
            selectedEmailNotification !== undefined 
        ) {
            return true
        }

        return false
    }

    const SaveChanges = () => {
        clearBanner()
        const user = {
            name: firstName,
            lastName,
            phoneNumber: phone,
            email: email,
            notifications: selectedEmailNotification === 'true',
            contactMe: contactMe,
            questions: {
                mailProductBalancing: companyStatementTypes[1].mail,
                electronicProductBalancing: companyStatementTypes[1].electronic,
                mailPayout: companyStatementTypes[2].mail,
                electronicPayout: companyStatementTypes[2].electronic,
                mailJIBInvoices: companyStatementTypes[3].mail,
                electronicJIBInovices: companyStatementTypes[3].electronic,
                mailRevenueChecks: companyStatementTypes[4].mail,
                electronicRevenueChecks: companyStatementTypes[4].electronic,
                mailOther: companyStatementTypes[5].mail,
                electronicOther: companyStatementTypes[5].electronic,
                other: companyStatementTypes[5].other,
                accountingSoftware: accountingSoftware
            }
        }

        axios
            .put('/UpdateUser', user)
            .then((response) => {
                if (response.status === 200) {
                    navigate('/dashboard')
                }
            })
            .catch((err) => {
                displayErrorMessage('There was an error updating the information. Please retry.')
            })
    }

    const handleCancelButton = () => {
        
    }

    useEffect(() => {
        setIsLoading(true)
        clearBanner()
        axios
            .get('GetUser')
            .then(response => {
                if (response.status != 200) {
                    displayErrorMessage('There was an error retrieving user information.')
                    return;
                }
                const user = response.data
                setFirstName(user.name)
                setLastName(user.lastName)
                setPhone(user.phoneNumber)
                setEmail(user.email)
                setSelectedEmailNotification(user.notifications)
                setContactMe(user.contactMe)
            })
            .catch(err => {
                console.error(err)
                displayErrorMessage('There was an error retrieving user information.')
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, [])

    const handleCompanyCheck = (key, type, checked) => {
        let tempCompanyStatementTypes = { ...companyStatementTypes }
        if (type == ORGANIZATION_PARTY_TYPE.MAIL) {
            tempCompanyStatementTypes[key].mail = checked
        }

        if (type == ORGANIZATION_PARTY_TYPE.ELECTRONIC) {
            tempCompanyStatementTypes[key].electronic = checked
        }

        setCompanyStatementTypes(tempCompanyStatementTypes)
    }

    const handleOtherInput = (value) => {
        let tempCompanyStatementTypes = { ...companyStatementTypes }
        tempCompanyStatementTypes[5].other = value
        setCompanyStatementTypes(tempCompanyStatementTypes)
    }

    const handleAccountingSoftware = (value) => {
        setAccountingSoftware(value)
    }

    return (
        <>
            <div className="mt-4 mx-2 setup-account-form">
                <h1>Welcome to EnergilityDX!</h1>
                {displayBanner && (
                    <Grid item xs={12}>
                        <AlertBanner backgroundColor={bannerStyle} isCloseHidden>
                            <AlertBannerText text={bannerMessage} />
                        </AlertBanner>
                    </Grid>
                )}
                <h3 className='mt-1'>Please update your user account details</h3>
                {!isLoading ? (
                    <>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={8} md={6} lg={3}>
                                <TextField
                                    id="email"
                                    value={email}
                                    onChange={(event) => setEmail(event)}
                                    title={(<><p style={{color:'black'}}>Email<b style={{color:'red'}}>*</b></p></>)}
                                    placeholder="Enter your email"
                                    size={TextField.sizes.SMALL}
                                    validation={{
                                        status:
                                            email == ''
                                                ? ''
                                                : !isValidEmail(email)
                                                ? 'error'
                                                : 'success',
                                        text:
                                            email == ''
                                                ? ''
                                                : !isValidEmail(email) &&
                                                    email.length
                                                ? 'Please enter a valid email address.'
                                                : '',
                                    }}
                                    maxLength={128}
                                    requiredAsterisk
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} sm={8} md={6} lg={9}></Grid>
                            <Grid item xs={12} sm={8} md={6} lg={3}>
                                <TextField
                                    id="first-name"
                                    value={firstName}
                                    onChange={(event) => setFirstName(event)}
                                    title={(<><p style={{color:'black'}}>First Name<b style={{color:'red'}}>*</b></p></>)}
                                    placeholder="Enter your first name"
                                    size={TextField.sizes.SMALL}
                                    validation={{
                                        status:
                                            firstName == ''
                                                ? ''
                                                : validateStringOnly(firstName)
                                                ? 'error'
                                                : 'success',
                                        text:
                                            firstName == ''
                                                ? ''
                                                : validateStringOnly(firstName)
                                                ? 'Please enter a valid First Name'
                                                : '',
                                    }}
                                    maxLength={128} //TBD: DB
                                />
                            </Grid>
                            <Grid item xs={12} sm={8} md={6} lg={9}></Grid>
                            <Grid item xs={12} sm={8} md={6} lg={3}>
                                <TextField
                                    id="last-name"
                                    value={lastName}
                                    onChange={(event) => setLastName(event)}
                                    title={(<><p style={{color:'black'}}>Last Name<b style={{color:'red'}}>*</b></p></>)}
                                    placeholder="Enter your last name"
                                    size={TextField.sizes.SMALL}
                                    validation={{
                                        status:
                                            lastName == ''
                                                ? ''
                                                : validateStringOnly(lastName)
                                                ? 'error'
                                                : 'success',
                                        text:
                                            lastName == ''
                                                ? ''
                                                : validateStringOnly(lastName)
                                                ? 'Please enter a valid Last Name'
                                                : '',
                                    }}
                                    maxLength={128} //TBD: DB
                                    requiredAsterisk
                                />
                            </Grid>
                            <Grid item xs={12} sm={8} md={6} lg={9}></Grid>
                            <Grid item xs={12} sm={8} md={6} lg={3}>
                                <TextField
                                    id="phone"
                                    value={phone}
                                    onChange={(event) => setPhone(event)}
                                    title={(<><p style={{color:'black'}}>Phone</p></>)}
                                    placeholder="Enter your phone number"
                                    size={TextField.sizes.SMALL}
                                    validation={{
                                        status:
                                            phone == ''
                                                ? ''
                                                : validateNumberOnly(phone)
                                                ? 'error'
                                                : 'success',
                                        text:
                                            phone == ''
                                                ? ''
                                                : validateNumberOnly(phone)
                                                ? 'Please enter a valid phone number.'
                                                : '',
                                    }}
                                    maxLength={128}
                                />
                            </Grid>
                            <Grid item xs={12} sm={8} md={6} lg={3}></Grid>
                        </Grid>
                        <h3 className='mt-4'>Email Notifications</h3>
                        <Grid container spacing={2} className="">
                            <Grid item xs={12} sm={8} md={6} lg={6}>
                                <p>
                                    EnergilityDX will send email notifications when new statements or information are available
                                    for you to view. 
                                    Your email address will only be used for these notifications in accordance with our <a href='https://energilitydx.com/privacy/'>Privacy Policy</a>. 
                                    You can change this setting at any time in your user account.
                                </p>
                            </Grid>

                            <Grid item xs={12}>
                                <RadioButton
                                    onSelect={handleEmailNotificationChange}
                                    checked={
                                        selectedEmailNotification ===
                                        'true'
                                    }
                                    value={true}
                                    text="Yes, please send me email notifications."
                                    name="email-notifications-radio-buttons"
                                />
                                <RadioButton
                                    onSelect={handleEmailNotificationChange}
                                    checked={
                                        selectedEmailNotification ===
                                        'false'
                                    }
                                    value={false}
                                    text="No, thanks. I do not want to receive email notifications."
                                    name="email-notifications-radio-buttons"
                                />
                            </Grid>
                        </Grid>
                        <>
                            <h3 className='mt-4'>About your company</h3>
                            <Grid item xs={10} sm={10} md={10} lg={10}>
                                <p>
                                    <span>What oil & gas accounting software does your company use?</span>
                                    <br/>
                                    <textarea
                                        style={{marginTop:'4px', resize: 'none'}} 
                                        value={accountingSoftware} 
                                        onChange={(event) => handleAccountingSoftware(event.target.value)} 
                                        rows={4} cols={90}
                                        resize='none'
                                    />                             
                                </p>
                            </Grid>
                            <Grid item xs={12}>
                                <p>
                                    Does your company send various types
                                    of statements to external parties
                                    (operators, owners, etc)?
                                </p>
                            </Grid>
                            <Grid
                                container
                                spacing={2}
                                className="mx-1"
                            >
                                <Grid item xs={0.5} sm={0.5} md={0} lg={0.5}>
                                    <p style={{marginTop:'16px'}}>
                                        <b>Mail</b>
                                    </p>
                                </Grid>
                                <Grid item xs={1} sm={1} md={1} lg={1}>
                                    <p style={{marginTop:'16px'}}>
                                        <b>Electronic</b>
                                    </p>
                                </Grid>
                                <Grid item xs={10} sm={10} md={10} lg={10}>
                                    <p style={{marginTop:'16px'}}>
                                        <b>Statement Type</b>
                                    </p>
                                </Grid>
                            </Grid>

                            {Object.keys(companyStatementTypes).map(
                                (key, index) => {
                                    const {
                                        label,
                                        mail,
                                        electronic,
                                        other,
                                    } = companyStatementTypes[key]

                                    return (
                                        <Grid
                                            container
                                            spacing={2}
                                            className="mx-1"
                                            key={key}
                                        >
                                            <Grid item xs={0.5} sm={0.5} md={0.5} lg={0.5}>
                                                <Checkbox
                                                    style={{paddingLeft:'2px', paddingTop:'4px', paddingBottom: '4px'}} 
                                                    checked={mail}
                                                    onChange={(event) =>
                                                        handleCompanyCheck(
                                                            key,
                                                            ORGANIZATION_PARTY_TYPE.MAIL,
                                                            event.target
                                                                .checked
                                                        )
                                                    }
                                                    inputProps={{
                                                        'aria-label':
                                                            'controlled',
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={1} sm={1} md={1} lg={1}>
                                                <Checkbox
                                                    style={{paddingLeft:'20px', paddingTop:'4px', paddingBottom: '4px'}}
                                                    checked={electronic}
                                                    onChange={(event) =>
                                                        handleCompanyCheck(
                                                            key,
                                                            ORGANIZATION_PARTY_TYPE.ELECTRONIC,
                                                            event.target
                                                                .checked
                                                        )
                                                    }
                                                    inputProps={{
                                                        'aria-label':
                                                            'controlled',
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={10} sm={10} md={6} lg={10}>
                                                <p style={{paddingTop:'5px'}}>
                                                    {label}
                                                    { 
                                                        key == 5 ? (
                                                        <>
                                                            <br/>
                                                            <textarea 
                                                                style={{resize: 'none'}} 
                                                                value={other} 
                                                                onChange={(event) => handleOtherInput(event.target.value)} 
                                                                rows={4} cols={50} 
                                                                resize='none'
                                                            />
                                                        </>
                                                        ) : <></> 
                                                    }
                                                </p>
                                            </Grid>
                                        </Grid>
                                    )
                                }
                            )}
                        </>
                        <Grid container spacing={2}>
                            <Grid item>
                                <Checkbox
                                        style={{paddingLeft:'1px', marginTop:'16px'}} 
                                        checked={contactMe}
                                        onChange={(event) =>
                                            setContactMe(event.target.checked)
                                        }
                                        inputProps={{ 'aria-label': 'controlled' }}
                                    />
                            </Grid>
                            <Grid item mt={1} style={{paddingLeft:'0px'}}>
                                <p style={{paddingLeft:'0px', paddingTop:'2px', marginTop:'16px'}}>
                                    I'd like the EnergilityDX team to contact me about sending statements through the EnergilityDX data exchange.
                                </p>
                            </Grid>
                        </Grid>
                        <Grid
                            container
                            direction="row"
                            justifyContent="flex-end"
                            alignItems="center"
                            className='mt-3 mb-3'
                            xs={12} sm={12} md={9} lg={6}
                        >
                            {/* <Grid item mr={2}>
                                <Button
                                    size={Button.sizes.SMALL}
                                    type={Button.types.BUTTON}
                                    onClick={handleCancelButton}
                                    className="cancel"
                                >
                                    Cancel
                                </Button>
                            </Grid> */}
                            
                            <Grid item>
                                <Button
                                    size={Button.sizes.SMALL}
                                    type={Button.types.BUTTON}
                                    onClick={SaveChanges}
                                    disabled={!validateSaveChanges()}
                                >
                                    Save
                                </Button>
                            </Grid>
                        </Grid>
                    </>
                ) : (
                    <Grid container spacing={2} className="">
                        <Grid item xs={12}>
                            <LoadingSpinner/>
                        </Grid>
                    </Grid>
                )}
            </div>
        </>
    )
}

export default SetupAccountForm